import { DOCUMENT } from '@angular/common';
import { Observable } from 'rxjs';
import { Component, ElementRef, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { TrackingsService } from '../../../../../services/tracking.service';
import { StockState } from '@toro/common/shared/state-management/stock/stock.reducer';
import { FinanceState } from '@toro/common/shared/state-management/finance/finance.reducer';
import { StockActions } from '@toro/common/shared/state-management/stock/stock.action';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
    selector: 'regular-note',
    templateUrl: './regular-note.component.html',
    styleUrls: ['./regular-note.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class RegularNoteComponent extends DialogPanelComponentContent implements OnInit{

    public stock$: Observable<StockState>;
    public pending: boolean = false;
    public finance$: Observable<FinanceState>;
    public receiptDate: Date;
    public formDatePicker: FormGroup;
    public isMobile: boolean;
    public datesInvestment: any[] = [];
    public brokerageDates: any[] = [];
    public brokerageNotesTypesActions = {
        stock: StockActions.getBrokageNoteStock,
        future: StockActions.getBrokageNoteFuture,
        option: StockActions.getBrokageNoteOption,
    };

    public constructor(
        public formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data,
        @Inject(DOCUMENT) private document: Document,
        public elementRef: ElementRef,
        private dialogReference: MatDialogRef<RegularNoteComponent>,
        private router: Router,
        private tracking: TrackingsService,
        private store: Store<AppState>,
        private utilitiesService: UtilitiesService,
    ) {
        super(elementRef, true);

        this.isMobile = this.utilitiesService.isMobile();
        this.stock$ = this.store.select((s) => s.stocks);
        this.pending = true;
        this.finance$ = this.store.select((s) => s.finance);
        this.datesInvestment = [];

        this.formDatePicker = this.formBuilder.group({
            formDatePickerField: ['', Validators.required]
        });

        this.startObservers();
    }

    public ngOnInit() {
        super.ngOnInit();
        // Dispatch após observers estarem prontos para evitar race condition
        this.store.dispatch({ type: StockActions.getNoteListDate });
    }

    /**
     * Encerra componente, habilita overflow e destroi dialogReference
     */
    public closeDialog() {
        this.document.body.classList.remove('freeze');
        this.close(this.dialogReference, null);
        this.dialogReference = null;
    }

     /**
     * Filtra data diretamente pelo input do datepicker
     *
     * @param date
     */
      public filterDatePicker = (date: Date | null): boolean => {
        if (!date) {
            return false;
        }

        const selectedDate = this.getFormatedDate(date);

        if (!this.datesInvestment || !this.datesInvestment.length) {
            return false;
        }

        return this.datesInvestment.includes(selectedDate);
    }

    /**
     * Realiza download dos comprovantes na data selecionada
     */
    public receiptDownload() {
        if (!this.receiptDate || !this.brokerageDates || !this.brokerageDates.length) {
            return;
        }

        const selectedDate = this.getFormatedDate(this.receiptDate);
        this.pending = true;

        // Filtra datas do array brokerageDates e dispara actions de download
        this.brokerageDates
            .filter((date) => this.getFormatedDate(date.referenceDate) === selectedDate && date.market)
            .map((date) => date.market.toLowerCase())
            .forEach((market) => {
                if (this.brokerageNotesTypesActions[market]) {
                    this.store.dispatch({
                        type: this.brokerageNotesTypesActions[market],
                        payload: selectedDate
                    });
                }
            });
    }

    public getFormatedDate(date: Date | string): string {
        if (!date) {
            return '';
        }

        try {
            // Se já é string ISO, usa direto
            if (typeof date === 'string') {
                return date.split('T')[0].split('-').join('/');
            }

            // Se é Date object
            return new Date(date).toISOString().split('T')[0].split('-').join('/');
        } catch (error) {
            console.error('Erro ao formatar data:', date, error);
            return '';
        }
    }

    public pushDates(dateList: any[], objectAttribute: string) {
        if (!dateList || !dateList.length) {
            this.datesInvestment = [];
            return;
        }

        const uniqueDates = new Set<string>();

        dateList.forEach((item) => {
            if (item && item[objectAttribute]) {
                const formattedDate = this.getFormatedDate(item[objectAttribute]);
                if (formattedDate) {
                    uniqueDates.add(formattedDate);
                }
            }
        });

        this.datesInvestment = Array.from(uniqueDates);
    }

    public startObservers() {
        this.store.select((stock) => ({
            pendingIncomeNotes: stock.stocks.pendingIncomeNotes,
            pending: stock.stocks.pending
        })).pipe(
            distinctUntilChanged((prev, curr) =>
                prev.pendingIncomeNotes === curr.pendingIncomeNotes &&
                prev.pending === curr.pending
            )
        ).subscribe(({ pendingIncomeNotes, pending }) => {
            this.pending = !!(pendingIncomeNotes || pending);
        });

        this.store.select((s) => s.stocks.incomeDates).pipe(
            filter(stockDates => !!stockDates && stockDates.length > 0)
        ).subscribe((stockDates) => {
            this.brokerageDates = stockDates;
            this.pushDates(stockDates, 'referenceDate');
        });
    }
}
